import { createAsyncThunk } from '@reduxjs/toolkit';

import { 
  getFacilityById as getFacilityByIdService,
  getFacilitiesByIds as getFacilitiesByIdsService,
  updateFacility as updateFacilityService,
  createFacility as createFacilityService,
  getAllFacilities as getAllFacilitiesService
} from '../../services/facilityService';

// Helper function to serialize Firestore timestamps
const serializeTimestamps = (obj) => {
  if (!obj) return obj;
  
  const newObj = { ...obj };
  
  Object.keys(newObj).forEach(key => {
    const value = newObj[key];
    
    // Check if value is a Firestore Timestamp
    if (value && typeof value === 'object' && value.seconds !== undefined && value.nanoseconds !== undefined) {
      // Convert Timestamp to ISO string
      newObj[key] = new Date(value.seconds * 1000 + value.nanoseconds / 1000000).toISOString();
    } 
    // Handle nested objects and arrays
    else if (value && typeof value === 'object') {
      if (Array.isArray(value)) {
        newObj[key] = value.map(item => 
          typeof item === 'object' ? serializeTimestamps(item) : item
        );
      } else {
        newObj[key] = serializeTimestamps(value);
      }
    }
  });
  
  return newObj;
};

// Fetch all facilities (admin only) - SECURE VERSION
export const fetchFacilities = createAsyncThunk(
  'facilities/fetchFacilities',
  async () => {
    try {
      console.log('fetchFacilities called with secure server function');
      
      // Use secure server-side function instead of direct Firestore queries
      const facilities = await getAllFacilitiesService();
      
      console.log(`Secure function returned ${facilities.length} facilities`);
      
      // Transform data to match the expected format
      const serializedFacilities = facilities.map(facility => serializeTimestamps(facility));
      
      return serializedFacilities;
    } catch (error) {
      console.error('Error in fetchFacilities:', error);
      // If it's a permission error, return empty array instead of failing completely
      if (error.message.includes('permission-denied') || 
          error.message.includes('Only admins can view all facilities')) {
        console.warn('User does not have permission to view all facilities, returning empty array');
        return [];
      }
      throw error;
    }
  }
);

// Fetch user facilities - SECURE VERSION (already secure)
export const fetchUserFacilities = createAsyncThunk(
  'facilities/fetchUserFacilities',
  async ({ page = 1, limit = 10 } = {}) => {
    console.log('fetchUserFacilities called with secure server function:', { page, limit });
    
    try {
      // Use secure server-side function instead of direct Firestore queries
      const { getUserFacilities } = await import('../../services/userService');
      const result = await getUserFacilities(page, limit);
      
      console.log(`Secure function returned ${result.facilities.length} of ${result.total} facilities`);
      
      // Transform data to match the expected format
      const serializedFacilities = result.facilities.map(facility => serializeTimestamps(facility));
      
      return {
        facilities: serializedFacilities,
        total: result.total,
        totalCount: result.total,
        currentPage: page,
        totalPages: result.totalPages,
        page,
        limit
      };
    } catch (error) {
      console.error('Error in fetchUserFacilities:', error);
      // If it's a timeout or network error, return empty array instead of failing completely
      if (error.message.includes('timeout') || 
          error.message.includes('network') ||
          error.code === 'unavailable') {
        console.warn('Facilities fetch failed, returning empty array. Error:', error.message);
        return {
          facilities: [],
          total: 0,
          totalCount: 0,
          currentPage: page,
          totalPages: 0,
          page,
          limit
        };
      }
      throw error;
    }
  },
  {
    condition: (_, { getState }) => {
      const state = getState();
      const loading = state.facilities.loading;
      const lastFetched = state.facilities.lastFetched;
      const userFacilities = state.facilities.userFacilities;
      const STALE_TIME = 10 * 60 * 1000; // 10 minutes
      const isStale = !lastFetched || (Date.now() - lastFetched > STALE_TIME);
      
      // Prevent duplicate fetch if already loading
      if (loading) {
        console.log('fetchUserFacilities: Skipping - already loading');
        return false;
      }
      
      // Skip if data is fresh and we have facilities
      if (!isStale && userFacilities && userFacilities.length > 0) {
        console.log('fetchUserFacilities: Skipping - data is fresh');
        return false;
      }
      
      console.log('fetchUserFacilities: Proceeding with fetch', { loading, isStale, facilitiesCount: userFacilities?.length || 0 });
      return true;
    }
  }
);

// Fetch facilities by IDs - SECURE VERSION
export const fetchFacilitiesByIds = createAsyncThunk(
  'facilities/fetchFacilitiesByIds',
  async (facilityIds) => {
    try {
      console.log('fetchFacilitiesByIds called with secure server function:', { facilityIds });
      
      // Filter out undefined/null values from facility IDs
      const validFacilityIds = facilityIds.filter(id => id !== undefined && id !== null);
      
      if (validFacilityIds.length === 0) {
        return [];
      }
      
      // Use secure server-side function instead of direct Firestore queries
      const facilities = await getFacilitiesByIdsService(validFacilityIds);
      
      console.log(`Secure function returned ${facilities.length} facilities`);
      
      // Transform data to match the expected format
      const serializedFacilities = facilities.map(facility => serializeTimestamps(facility));
      
      return serializedFacilities;
    } catch (error) {
      console.error('Error in fetchFacilitiesByIds:', error);
      // If it's a permission error, return empty array for facilities user can't access
      if (error.message.includes('permission-denied') || 
          error.message.includes('Access denied')) {
        console.warn('User does not have permission to access some facilities, returning empty array');
        return [];
      }
      throw error;
    }
  }
);

// Fetch a single facility by ID - SECURE VERSION
export const fetchFacilityById = createAsyncThunk(
  'facilities/fetchFacilityById',
  async (facilityId) => {
    try {
      console.log('fetchFacilityById called with secure server function:', { facilityId });
      
      // Use secure server-side function instead of direct Firestore queries
      const facility = await getFacilityByIdService(facilityId);
      
      console.log('Secure function returned facility:', facility.id);
      
      // Transform data to match the expected format
      return serializeTimestamps(facility);
    } catch (error) {
      console.error('Error in fetchFacilityById:', error);
      // If it's a permission error, throw a more specific error
      if (error.message.includes('permission-denied') || 
          error.message.includes('Access denied')) {
        throw new Error('You do not have permission to access this facility');
      }
      if (error.message.includes('not-found') || 
          error.message.includes('Facility not found')) {
        throw new Error('Facility not found');
      }
      throw error;
    }
  }
);

// Update a facility - SECURE VERSION
export const updateFacility = createAsyncThunk(
  'facilities/updateFacility',
  async ({ id, ...facilityData }, { rejectWithValue }) => {
    try {
      console.log('updateFacility called with secure server function:', { id, facilityData });
      
      // Use secure server-side function instead of direct Firestore queries
      const updatedFacility = await updateFacilityService(id, facilityData);
      
      console.log('Secure function updated facility:', updatedFacility.id);
      
      // Transform data to match the expected format
      return serializeTimestamps(updatedFacility);
    } catch (error) {
      console.error('Error in updateFacility:', error);
      
      // Handle specific error types
      if (error.message.includes('permission-denied')) {
        return rejectWithValue('You do not have permission to update this facility');
      }
      if (error.message.includes('not-found')) {
        return rejectWithValue('Facility not found');
      }
      if (error.message.includes('invalid-argument')) {
        return rejectWithValue('Invalid facility data provided');
      }
      
      return rejectWithValue(error.message || 'Failed to update facility');
    }
  }
);

// Create a new facility - SECURE VERSION
export const createFacility = createAsyncThunk(
  'facilities/createFacility',
  async (facilityData, { rejectWithValue }) => {
    try {
      console.log('createFacility called with secure server function:', { facilityData });
      
      if (!facilityData.name) {
        return rejectWithValue('Facility name is required');
      }

      // Use secure server-side function instead of direct Firestore queries
      const createdFacility = await createFacilityService(facilityData);
      
      console.log('Secure function created facility:', createdFacility.id);
      
      // Transform data to match the expected format
      return serializeTimestamps(createdFacility);
    } catch (error) {
      console.error('Error in createFacility:', error);
      
      // Handle specific error types
      if (error.message.includes('permission-denied')) {
        return rejectWithValue('You do not have permission to create facilities');
      }
      if (error.message.includes('invalid-argument')) {
        return rejectWithValue('Invalid facility data provided');
      }
      if (error.message.includes('unauthenticated')) {
        return rejectWithValue('You must be logged in to create facilities');
      }
      
      return rejectWithValue(error.message || 'Failed to create facility');
    }
  }
);
